/**
 * The keepalive: the client's `ping`, the server's `heartbeat`, and the
 * liveness verdict.
 *
 * **Application-level ping, not WebSocket control frames.** Browsers cannot send
 * protocol-level pings from JavaScript, so any scheme built on them is unusable
 * from a web client (docs/04 §9.1). The client sends `{"type":"ping"}` on a
 * timer and the server replies `pong`; the server independently pushes a
 * `heartbeat` frame every 5 s from the instant the socket opens.
 *
 * This is the one recurring timer the transport owns, and it is a row in docs/03
 * Appendix B — which is why this file is on the `setInterval` allowlist in
 * `eslint.config.js` and why adding a second timer here needs a row in that
 * table first.
 *
 * Three jobs, and the second is the one that pays for the file:
 *
 * 1. **Liveness.** Two consecutive missed `pong`s is a dead connection.
 *    Reconnect on the second miss rather than waiting for a TCP timeout that can
 *    take minutes — on a 60-second market, minutes is several markets.
 * 2. **The clock.** `PongData.recv_time_ms` is annotated in `asyncapi` as "`t2`
 *    in the NTP 4-timestamp set (D9)". With the echoed `client_time_ms`, the
 *    pong's own `server_time_ms` and the client's receive stamp, that is the
 *    same four timestamps `GET /time` yields — an unbiased offset sample every
 *    5 seconds, over a socket that is already open, at no extra request
 *    (register §F11e). `connection.ts` routes the frame; this file makes sure
 *    one is asked for on a cadence.
 * 3. **The hidden-tab cadence.** A hidden tab pings at
 *    `hidden_tab_ping_interval_ms` (20 s), not slower, and both intervals come
 *    from `config.ws`. The server closes a connection it has heard nothing from
 *    for 75 s; browsers throttle hidden-tab timers to ≥60 s, so a 30 s ceiling
 *    would disconnect every backgrounded tab on a timer — while the client is
 *    deliberately keeping its private subscriptions so notifications and
 *    settlements still land.
 */

import type { AnyServerFrame, HeartbeatData } from './protocol'
import { WS_LIMITS } from './protocol'
import type { WsConnection } from './connection'

export interface HeartbeatHandlers {
  /**
   * Two consecutive pings went unanswered. The socket is dead whatever it
   * claims; the connection reconnects on the caller's word, not this file's.
   */
  onDead?: (missed: number) => void
  /** A server `heartbeat` frame. Carries `config_revision`, `mode` and
   *  `venue_status`, none of which the transport interprets. */
  onHeartbeat?: (data: HeartbeatData) => void
  /**
   * The tab became visible after `hiddenMs` hidden. Over a minute means the
   * wall clock may have jumped (a slept laptop) and the panels are certainly
   * stale — docs/03 §9.2.4 is the caller's list.
   */
  onVisible?: (hiddenMs: number) => void
  onHidden?: () => void
}

export interface HeartbeatOptions extends HeartbeatHandlers {
  connection: WsConnection
  /** Injected so a test drives a whole minute in microseconds. */
  setTimer?: (fn: () => void, ms: number) => number
  clearTimer?: (handle: number) => void
  monoNow?: () => number
  /** Injected: jsdom has a `document`, a headless harness may not. */
  visibility?: VisibilitySource
  missedPongsBeforeDead?: number
}

/** Just enough of `document` to know whether the tab is being watched. */
export interface VisibilitySource {
  isHidden(): boolean
  subscribe(listener: () => void): () => void
}

/** The browser's. Returns a source that reports "visible" when there is no
 *  document at all, which is the right answer for a headless harness. */
export function documentVisibility(): VisibilitySource {
  const doc = globalThis.document as Document | undefined
  if (!doc) {
    return { isHidden: () => false, subscribe: () => () => {} }
  }
  return {
    isHidden: () => doc.visibilityState === 'hidden',
    subscribe: (listener) => {
      doc.addEventListener('visibilitychange', listener)
      return () => doc.removeEventListener('visibilitychange', listener)
    },
  }
}

export class Heartbeat {
  readonly #connection: WsConnection
  readonly #setTimer: (fn: () => void, ms: number) => number
  readonly #clearTimer: (handle: number) => void
  readonly #monoNow: () => number
  readonly #visibility: VisibilitySource
  readonly #missedBeforeDead: number
  readonly #handlers: HeartbeatHandlers

  #handle: number | null = null
  #unsubscribeVisibility: (() => void) | null = null
  /** Correlation ids of pings sent and not yet answered. */
  #outstanding = new Set<string>()
  #hidden = false
  #hiddenSinceMs: number | null = null
  #intervalMs = 0
  #pingsSent = 0
  #pongsReceived = 0
  #lastHeartbeatAtMs: number | null = null

  constructor(options: HeartbeatOptions) {
    this.#connection = options.connection
    this.#setTimer = options.setTimer ?? ((fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number)
    this.#clearTimer = options.clearTimer ?? ((h) => globalThis.clearTimeout(h))
    this.#monoNow = options.monoNow ?? (() => performance.now())
    this.#visibility = options.visibility ?? documentVisibility()
    this.#missedBeforeDead = options.missedPongsBeforeDead ?? WS_LIMITS.missedPongsBeforeDead
    this.#handlers = options
  }

  get outstandingPings(): number {
    return this.#outstanding.size
  }

  get pingsSent(): number {
    return this.#pingsSent
  }

  get pongsReceived(): number {
    return this.#pongsReceived
  }

  get intervalMs(): number {
    return this.#intervalMs
  }

  get hidden(): boolean {
    return this.#hidden
  }

  /** Server heartbeat frames are the socket's other liveness signal; a client
   *  that has seen neither pong nor beat is not guessing about much. */
  get lastHeartbeatAtMs(): number | null {
    return this.#lastHeartbeatAtMs
  }

  /**
   * Start pinging. Called on every `open`, because the outstanding-ping set from
   * the previous socket describes a connection that no longer exists.
   */
  start(): void {
    this.stop()
    this.#outstanding.clear()
    this.#hidden = this.#visibility.isHidden()
    this.#unsubscribeVisibility = this.#visibility.subscribe(() => this.#onVisibilityChange())
    this.#arm()
  }

  stop(): void {
    if (this.#handle !== null) {
      this.#clearTimer(this.#handle)
      this.#handle = null
    }
    this.#unsubscribeVisibility?.()
    this.#unsubscribeVisibility = null
  }

  /** Route a frame. Returns `true` if it was a keepalive frame and needs no
   *  further handling by the caller. */
  observe(frame: AnyServerFrame): boolean {
    if (frame.type === 'pong') {
      this.#pongsReceived += 1
      // Clear by correlation id when there is one; a server that answers without
      // echoing the id still proves the socket is alive, so the whole set goes.
      if (typeof frame.id === 'string' && this.#outstanding.has(frame.id)) this.#outstanding.delete(frame.id)
      else this.#outstanding.clear()
      return true
    }
    if (frame.type === 'heartbeat') {
      this.#lastHeartbeatAtMs = this.#monoNow()
      if (frame.data) this.#handlers.onHeartbeat?.(frame.data as HeartbeatData)
      return true
    }
    return false
  }

  #arm(): void {
    const config = this.#connection.config
    this.#intervalMs = this.#hidden ? config.hidden_tab_ping_interval_ms : config.ping_interval_ms
    if (this.#handle !== null) this.#clearTimer(this.#handle)
    // A self-rescheduling timeout rather than `setInterval`: the cadence changes
    // when the tab hides, and an interval would have to be torn down and rebuilt
    // anyway. This also means a throttled tab cannot accumulate a backlog of
    // queued pings that all fire at once on wake.
    this.#handle = this.#setTimer(() => this.#beat(), this.#intervalMs)
  }

  #beat(): void {
    this.#handle = null
    if (!this.#connection.isOpen) {
      // Nothing to ping. The connection is either backing off or shut down, and
      // in both cases it will call `start()` again when it has a socket.
      return
    }

    if (this.#outstanding.size >= this.#missedBeforeDead) {
      const missed = this.#outstanding.size
      this.#outstanding.clear()
      this.#handlers.onDead?.(missed)
      return
    }

    const id = this.#connection.send('ping', {})
    if (id !== null) {
      this.#outstanding.add(id)
      this.#pingsSent += 1
    }
    this.#arm()
  }

  #onVisibilityChange(): void {
    const hidden = this.#visibility.isHidden()
    if (hidden === this.#hidden) return
    this.#hidden = hidden

    if (hidden) {
      this.#hiddenSinceMs = this.#monoNow()
      this.#handlers.onHidden?.()
    } else {
      const hiddenMs = this.#hiddenSinceMs === null ? 0 : this.#monoNow() - this.#hiddenSinceMs
      this.#hiddenSinceMs = null
      // Ping immediately on wake rather than waiting out a 20-second interval:
      // the first thing a returning user needs is a clock sample and a live
      // socket, and this is the cheapest way to learn the socket died while the
      // tab was throttled.
      this.#beatNow()
      this.#handlers.onVisible?.(hiddenMs)
    }
    this.#arm()
  }

  #beatNow(): void {
    if (!this.#connection.isOpen) return
    const id = this.#connection.send('ping', {})
    if (id !== null) {
      this.#outstanding.add(id)
      this.#pingsSent += 1
    }
  }
}
